import type { AuditRunDetail, ConversationVerdict } from '@owl/shared';
import { Fragment, useEffect, useState } from 'react';
import type { ShellCtx } from '../components/Shell';
import { formatUsd } from '../components/charts/AiUsageBlocks';
import { Icon } from '../components/icons';
import { activateOnKey } from '../lib/activateOnKey';
import { ApiError, api } from '../lib/api';
import { formatDateTime } from '../lib/time';

const VERDICT_COLOR: Record<ConversationVerdict, string> = {
  pass: '#2e9e5b',
  partial: '#c9921f',
  fail: '#d9534f',
  na: '#888',
};

function VerdictBadge({ verdict }: { verdict: ConversationVerdict | null }) {
  if (!verdict) return <span className="dim">—</span>;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: '#fff',
        background: VERDICT_COLOR[verdict],
      }}
    >
      {verdict}
    </span>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: 'var(--bg-2)',
        border: '1px solid var(--border-1)',
        borderRadius: 10,
        padding: '12px 14px',
        minWidth: 130,
      }}
    >
      <div className="dim" style={{ fontSize: 11, marginBottom: 4 }}>
        {label}
      </div>
      <div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}

export function AuditDetail({ ctx, runId }: { ctx: ShellCtx; runId: string }) {
  const [run, setRun] = useState<AuditRunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Poll while the run is still in progress so the progress bar advances live.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch on refreshTick
  useEffect(() => {
    let active = true;
    let timer: number | undefined;

    const load = () => {
      api
        .getAuditRun(runId)
        .then((res) => {
          if (!active) return;
          setRun(res);
          if (res.status === 'running') {
            timer = window.setTimeout(load, 2500);
          }
        })
        .catch((err) => {
          if (active) setError(err instanceof ApiError ? err.message : 'Failed to load');
        });
    };
    load();

    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [runId, ctx.refreshTick]);

  const scored = run ? run.passCount + run.partialCount + run.failCount : 0;
  const passPct = scored > 0 && run ? Math.round((100 * run.passCount) / scored) : 0;

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <button
            type="button"
            className="nav-item"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6 }}
            onClick={() => ctx.navigate({ name: 'audits' })}
          >
            <Icon.ChevronLeft size={13} /> Audits
          </button>
          <div className="view-title">
            {run?.label || (run ? formatDateTime(run.createdAt) : 'Audit run')}
          </div>
          {run && (
            <div className="view-sub">
              {formatDateTime(run.createdAt)} · {run.applicationName ?? 'all apps'} ·{' '}
              <span className="mono">{run.model}</span>
              {run.status === 'running' && ` · running ${run.processed}/${run.total}`}
              {run.status === 'error' && ` · error: ${run.error ?? 'unknown'}`}
            </div>
          )}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {run === null && !error ? (
        <div className="empty-state">Loading…</div>
      ) : run ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
            <Stat label="Progress" value={`${run.processed}/${run.total}`} />
            <Stat label="Pass rate" value={`${passPct}%`} />
            <Stat
              label="P / Pa / F / na / err"
              value={`${run.passCount} / ${run.partialCount} / ${run.failCount} / ${run.naCount} / ${run.errorCount}`}
            />
            <Stat label="Avg score" value={run.avgScore === null ? '—' : String(run.avgScore)} />
            <Stat label="Judge cost" value={formatUsd(run.costUsd)} />
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Conversation</th>
                  <th>Verdict</th>
                  <th>Score</th>
                  <th>Turns</th>
                  <th>Issues</th>
                </tr>
              </thead>
              <tbody>
                {run.results.map((res) => {
                  const isOpen = expanded === res.id;
                  return (
                    <Fragment key={res.id}>
                      <tr
                        className="data-row"
                        tabIndex={0}
                        onClick={() => setExpanded(isOpen ? null : res.id)}
                        onKeyDown={(e) =>
                          activateOnKey(e, () => setExpanded(isOpen ? null : res.id))
                        }
                      >
                        <td>
                          <span className="mono" style={{ fontSize: 12 }}>
                            {res.conversationOutcome ?? '—'}
                          </span>
                          {res.conversationChannel && (
                            <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
                              {res.conversationChannel}
                            </span>
                          )}
                        </td>
                        <td>
                          {res.status === 'error' ? (
                            <span style={{ fontSize: 12, color: 'var(--danger, #d9534f)' }}>
                              error
                            </span>
                          ) : (
                            <VerdictBadge verdict={res.verdict} />
                          )}
                        </td>
                        <td className="mono">{res.score ?? '—'}</td>
                        <td className="mono">{res.conversationTurns}</td>
                        <td>{res.issues.length > 0 ? res.issues.length : '—'}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={5} style={{ background: 'var(--bg-2)' }}>
                            {res.status === 'error' ? (
                              <div style={{ fontSize: 12.5, color: 'var(--danger, #d9534f)' }}>
                                {res.error ?? 'Judge failed'}
                              </div>
                            ) : (
                              <>
                                {res.reasoning && (
                                  <div style={{ marginBottom: 8 }}>
                                    <strong style={{ fontSize: 12 }}>Judge:</strong>{' '}
                                    <span style={{ fontSize: 12.5 }}>{res.reasoning}</span>
                                  </div>
                                )}
                                {res.issues.length > 0 && (
                                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
                                    {res.issues.map((iss) => (
                                      <li key={iss}>{iss}</li>
                                    ))}
                                  </ul>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
